import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import {
  PartnerDealGateResponse,
  PartnerDealGatesResponse,
} from '@interfaces/partners/responses/partner-deal-gates.response';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { provideUnscopedCompanyScope } from '@services/helpers/unscoped-company-scope';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  GATE_RESULT_LABELS,
  formatGateNumber,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-gates-panel/deal-gates-panel.component';

/**
 * Gates que já têm número, na ordem do servidor. Gate sem número (empresa sem DRE extraída) não entra:
 * o resumo da Visão geral só mostra o que já foi coletado (card 3380).
 */
export function gatesWithValue(gates: PartnerDealGatesResponse | null): PartnerDealGateResponse[] {
  return (gates?.gates ?? []).filter(gate => gate.value != null);
}

/**
 * Resumo dos gates na coluna lateral da Visão geral (card 3380): "Gates · valores internos", uma linha
 * por gate com o número e o selo. É o mesmo `GET partners/deals/{id}/gates` do painel completo da etapa
 * Gates, consultado uma vez, sem o acompanhamento do processamento.
 *
 * - Só o time interno: quem decide é o pai (`visaoInterna`), e o endpoint já é interno.
 * - **Sem número nenhum, não aparece**: nem cartão vazio, nem "pendente". Falha de carga também some,
 *   porque o resumo é acessório; o painel completo da etapa Gates mostra o erro e o "Tentar de novo".
 */
@Component({
  selector: 'app-deal-gates-summary',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './deal-gates-summary.component.html',
  styleUrl: './deal-gates-summary.component.scss',
  providers: provideUnscopedCompanyScope([PartnersHttpService]),
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealGatesSummaryComponent implements OnChanges {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly destroyRef = inject(DestroyRef);

  /** Deal exibido. */
  readonly dealId = input.required<string>();

  protected readonly gates = signal<PartnerDealGatesResponse | null>(null);
  protected readonly rows = computed(() => gatesWithValue(this.gates()));
  protected readonly labels = GATE_RESULT_LABELS;

  ngOnChanges(): void {
    this.partnersHttp
      .getDealGates(this.dealId())
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(gates => this.gates.set(gates));
  }

  /**
   * Rótulo do gate no cartão. O de faturamento vira "Porte (faturamento)" (resposta do Victor na página de
   * perguntas do card 3380); o painel completo da etapa Gates continua com o rótulo do servidor.
   */
  protected labelOf(gate: PartnerDealGateResponse): string {
    return gate.key === 'net-revenue' ? 'Porte (faturamento)' : gate.label;
  }

  protected describeValue(gate: PartnerDealGateResponse): string {
    return gate.value == null ? '—' : formatGateNumber(gate.value, gate);
  }
}
